import { useId, useMemo } from "react";

import type { AccessRole } from "@/modules/dashboard/settings/types/access.types";
import { ColocaCombobox } from "@/shared/components/forms/ColocaCombobox";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { useTranslations } from "@/shared/lang/useTranslations";

import { ACCESS_FILTER_ALL } from "../constants/access.constants";

export interface ApiKeysToolbarProps {
  query: string;
  onQueryChange: (value: string) => void;
  roleId: string;
  onRoleChange: (value: string) => void;
  roles: readonly AccessRole[];
  /** The roles catalog 403'd: filtering by a role we cannot name is not something to offer. */
  rolesUnavailable: boolean;
}

/**
 * Search plus the role filter — the same two controls the Settings section has, now over the whole loaded
 * catalog instead of a 50-key window.
 *
 * **No status control**: the tile strip above is that filter. **No organization filter**: `GET /api-keys`
 * takes no `organization_id`, and a client-side one would silently describe only the keys in memory while the
 * truncation notice says otherwise. A `ColocaCombobox` rather than a plain select because the roles catalog
 * is 86 entries and climbing.
 */
export function ApiKeysToolbar({
  query,
  onQueryChange,
  roleId,
  onRoleChange,
  roles,
  rolesUnavailable,
}: ApiKeysToolbarProps) {
  const { t } = useTranslations("settings");
  const roleFieldId = useId();

  const roleOptions = useMemo(
    () => [
      { value: ACCESS_FILTER_ALL, label: t("access_filter_all_roles") },
      ...roles.map((role) => ({ value: role.id, label: role.name })),
    ],
    [roles, t],
  );

  return (
    <ColocaSurface padding="md">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="w-full lg:max-w-sm">
          <ColocaSearchInput
            value={query}
            onValueChange={onQueryChange}
            placeholder={t("access_apikeys_search_placeholder")}
            aria-label={t("access_apikeys_search_placeholder")}
          />
        </div>

        {/* Absent, not disabled, when the catalog could not be read: an inert control implies it might work. */}
        {!rolesUnavailable && (
          <div className="flex w-full items-center gap-2 lg:w-auto">
            <label htmlFor={roleFieldId} className="shrink-0 text-xs text-muted-foreground">
              {t("access_role_filter_placeholder")}
            </label>
            <ColocaCombobox
              id={roleFieldId}
              value={roleId}
              onValueChange={(next) => onRoleChange(next ?? ACCESS_FILTER_ALL)}
              options={roleOptions}
              placeholder={t("access_filter_all_roles")}
              className="w-full sm:w-64"
            />
          </div>
        )}
      </div>
    </ColocaSurface>
  );
}
